import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Navbar, Nav, Spinner } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  notificationService,
  NotificationPreferences,
  loadUnreadNotificationCount,
} from '../services/api';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import { isUnlimitedUserType } from './LivesDisplay';
import { translateUserApiErrorMessage } from '../utils/translateUserApiError';
import { useQuizSoundPreference } from '../contexts/QuizSoundPreferenceContext';
import { useAppBackgroundMusic } from '../contexts/AppBackgroundMusicContext';
import {
  APP_BGM_PREFERENCE_SYNC_EVENT,
  getAppBgmEnabled,
  setAppBgmEnabledLocal,
} from '../audio/appBgmPreference';
import './UserProfile.css';

const SettingsPage: React.FC = () => {
  const { t } = useSafeTranslation();
  const navigate = useNavigate();
  const { soundsEnabled, setSoundsEnabled, busy: quizSoundBusy } = useQuizSoundPreference();
  const { isAvailable: bgmTracksAvailable } = useAppBackgroundMusic();
  const [bgmPrefEnabled, setBgmPrefEnabled] = useState(() => getAppBgmEnabled());
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(false);
  const [notifPrefsSaving, setNotifPrefsSaving] = useState(false);
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    name: ''
  });

  useEffect(() => {
    const syncBgmPref = () => setBgmPrefEnabled(getAppBgmEnabled());
    window.addEventListener(APP_BGM_PREFERENCE_SYNC_EVENT, syncBgmPref);
    return () => window.removeEventListener(APP_BGM_PREFERENCE_SYNC_EVENT, syncBgmPref);
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      if (token) {
        localStorage.setItem('authToken', token);
      }

      try {
        const userData = await authService.getProfile();
        setUser(userData);
        setUnreadNotificationCount(await loadUnreadNotificationCount(userData));
        setFormData({
          nickname: userData.nickname || '',
          email: userData.email,
          name: userData.name
        });
        localStorage.setItem('user', JSON.stringify(userData));
        setNotifPrefsLoading(true);
        try {
          const prefs = await notificationService.getPreferences();
          setNotifPrefs(prefs);
        } catch {
          setNotifPrefs(null);
        } finally {
          setNotifPrefsLoading(false);
        }
      } catch {
        /* ignore */
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [token]);

  useEffect(() => {
    if (user) {
      loadPendingRequestsCount();
    }
  }, [user]);

  const loadPendingRequestsCount = async () => {
    try {
      const requests = await friendshipService.getPendingRequests();
      setPendingRequestsCount(requests.length);
    } catch {
      /* ignore */
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError(t('user.nameCannotBeEmpty', 'Name cannot be empty.'));
      setIsSaving(false);
      return;
    }

    if (!formData.nickname.trim()) {
      setError(t('user.nicknameCannotBeEmpty', 'Nickname cannot be empty.'));
      setIsSaving(false);
      return;
    }

    try {
      let updatedUser = user;
      let hasChanges = false;

      if (formData.name !== user?.name) {
        updatedUser = await authService.updateName(formData.name);
        hasChanges = true;
      }

      if (formData.nickname !== user?.nickname) {
        updatedUser = await authService.updateNickname(formData.nickname);
        hasChanges = true;
      }

      if (hasChanges && updatedUser) {
        setUser(updatedUser);
        setSuccess(t('user.profileUpdated', 'Profile updated successfully!'));
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        setSuccess(t('user.noChangesToSave', 'No changes to save.'));
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError(
          t('user.nicknameAlreadyTaken', 'This nickname is already taken. Please choose a different one.')
        );
      } else {
        const raw = err.response?.data?.message as string | undefined;
        const localized = translateUserApiErrorMessage(raw, t);
        setError(
          localized ??
            t('user.errorUpdatingProfile', 'Error updating profile. Please try again.')
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  const handleRequestResponded = () => {
    loadPendingRequestsCount();
  };

  const handleNotifPrefChange = (key: keyof NotificationPreferences, value: boolean) => {
    setNotifPrefs((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const saveNotificationPreferences = async () => {
    if (!notifPrefs || !user) return;
    setNotifPrefsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await notificationService.updatePreferences({ ...notifPrefs, adminBroadcast: true });
      setSuccess(t('notifications.prefsSaved', 'Notification preferences saved.'));
    } catch {
      setError(t('notifications.prefsError', 'Could not save notification preferences.'));
    } finally {
      setNotifPrefsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-loading">
        <div className="text-center">
          <p>{t('user.userNotFound', 'User not found. Redirecting to login...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            className="navbar-brand d-flex align-items-center"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            <NotificationBell
              className="d-lg-none"
              unreadCount={unreadNotificationCount}
              onUnreadCountChange={setUnreadNotificationCount}
            />
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
              </div>
              <UserNavbarMenus
                user={user}
                pendingRequestsCount={pendingRequestsCount}
                unreadNotificationCount={unreadNotificationCount}
                onUnreadNotificationCountChange={setUnreadNotificationCount}
                onOpenFriends={() => setShowFriendsList(true)}
                onOpenPending={() => setShowPendingRequests(true)}
                onLogout={handleLogout}
              />
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="profile-card">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div className="profile-avatar mb-3">
                    <i className="bi bi-gear"></i>
                  </div>
                  <h2>{t('common.settings', 'Settings')}</h2>
                  <p className="text-muted">{t('user.updateAccountInfo', 'Update your account information')}</p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" className="mb-3">
                    {success}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('user.email', 'Email')}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      {t('user.emailCannotBeChanged', 'Email cannot be changed')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('user.name', 'Name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('user.enterYourName', 'Enter your name')}
                      maxLength={50}
                      className={error && !formData.name.trim() ? 'is-invalid' : ''}
                    />
                    <Form.Text className="text-muted">
                      {t('user.nameValidation', '2-50 characters, letters, spaces, hyphens and apostrophes only')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>{t('user.nickname', 'Nickname')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="nickname"
                      value={formData.nickname}
                      onChange={handleInputChange}
                      placeholder={t('user.enterYourNickname', 'Enter your nickname')}
                      maxLength={30}
                      className={error && !formData.nickname.trim() ? 'is-invalid' : ''}
                    />
                    <Form.Text className="text-muted">
                      {t('user.nicknameValidation', '3-30 characters, letters, numbers, underscores and hyphens only')}
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSaving || !formData.name.trim() || !formData.nickname.trim()}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t('common.saving', 'Saving...')}
                        </>
                      ) : (
                        t('common.saveChanges', 'Save Changes')
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => navigate('/profile')}
                    >
                      <i className="bi bi-person me-2"></i>
                      {t('common.profile', 'My Profile')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => navigate('/')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      {t('navigation.backToHome', 'Back to Home')}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            <Card className="profile-card mt-4">
              <Card.Body className="p-4">
                <h3 className="h5 mb-3">
                  <i className="bi bi-translate me-2" aria-hidden />
                  {t('user.languagePreference', 'Language Preference')}
                </h3>
                <p className="text-muted small mb-3">
                  {t(
                    'language.settingsHint',
                    'Choose the interface language. The change applies to the entire app.'
                  )}
                </p>
                <LanguageSelector user={user} isInline={false} flagsInForm />
              </Card.Body>
            </Card>

            <Card className="profile-card mt-4">
              <Card.Body className="p-4">
                <h3 className="h5 mb-3">
                  <i className="bi bi-volume-up me-2" aria-hidden />
                  {t('quiz.soundSettingsTitle', 'Sounds')}
                </h3>
                <p className="text-muted small mb-3">
                  {t(
                    'quiz.soundSettingsHint',
                    'Correct, incorrect, celebration, and informational sounds.'
                  )}
                </p>
                <Form.Check
                  type="switch"
                  id="settings-quiz-sounds"
                  className="mb-3"
                  checked={soundsEnabled}
                  onChange={(e) => void setSoundsEnabled(e.target.checked)}
                  disabled={quizSoundBusy}
                  label={t('quiz.soundSettingsSwitch', 'Play sound effects')}
                />
                <Form.Check
                  type="switch"
                  id="settings-bgm"
                  checked={bgmPrefEnabled}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setAppBgmEnabledLocal(on);
                    setBgmPrefEnabled(on);
                    window.dispatchEvent(new CustomEvent(APP_BGM_PREFERENCE_SYNC_EVENT));
                  }}
                  disabled={!bgmTracksAvailable}
                  title={
                    !bgmTracksAvailable
                      ? t('navigation.bgmNoTracks', 'No background music configured')
                      : undefined
                  }
                  label={t('quiz.musicSettingsSwitch', 'Play background music')}
                />
              </Card.Body>
            </Card>

            <Card className="profile-card mt-4">
              <Card.Body className="p-4">
                  <h3 className="h5 mb-3">
                    <i className="bi bi-bell me-2"></i>
                    {t('notifications.settingsSectionTitle', 'Notifications')}
                  </h3>
                  <p className="text-muted small">
                    {t(
                      'notifications.settingsSectionHint',
                      'Choose which kinds of in-app messages you want to receive.'
                    )}
                  </p>
                  {notifPrefsLoading && (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                  {notifPrefs && !notifPrefsLoading && (
                    <Form>
                      <Form.Check
                        type="switch"
                        id="pref-friends"
                        className="mb-2"
                        label={t('notifications.cat.friends', 'Friends & social')}
                        checked={notifPrefs.friendship}
                        onChange={(e) => handleNotifPrefChange('friendship', e.target.checked)}
                      />
                      <Form.Check
                        type="switch"
                        id="pref-prog"
                        className="mb-2"
                        label={t('notifications.cat.progression', 'Level & progression')}
                        checked={notifPrefs.progression}
                        onChange={(e) => handleNotifPrefChange('progression', e.target.checked)}
                      />
                      <Form.Check
                        type="switch"
                        id="pref-ach"
                        className="mb-2"
                        label={t('notifications.cat.achievements', 'Achievements')}
                        checked={notifPrefs.achievements}
                        onChange={(e) => handleNotifPrefChange('achievements', e.target.checked)}
                      />
                      {!isUnlimitedUserType(user?.userType) && (
                        <Form.Check
                          type="switch"
                          id="pref-lives"
                          className="mb-2"
                          label={t('notifications.cat.lives', 'Lives (full refill)')}
                          checked={notifPrefs.lives}
                          onChange={(e) => handleNotifPrefChange('lives', e.target.checked)}
                        />
                      )}
                      <Form.Check
                        type="switch"
                        id="pref-pvp"
                        className="mb-3"
                        label={t('notifications.cat.pvp', 'PvP')}
                        checked={notifPrefs.pvp}
                        onChange={(e) => handleNotifPrefChange('pvp', e.target.checked)}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={notifPrefsSaving}
                        onClick={() => void saveNotificationPreferences()}
                      >
                        {notifPrefsSaving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('common.saving', 'Saving...')}
                          </>
                        ) : (
                          t('notifications.savePrefs', 'Save notification preferences')
                        )}
                      </Button>
                    </Form>
                  )}
                </Card.Body>
              </Card>
          </Col>
        </Row>
      </Container>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}

      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={handleRequestResponded}
        />
      )}
    </div>
  );
};

export default SettingsPage;
